import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchConfig, isPortalNotFound } from '../api/config';
import { getPortalPathInfo } from '../utils/portalPath';
import type { ThemeConfig } from '../types/schema';

type RGB = { r: number; g: number; b: number };

function hexToRgb(value: string): RGB | null {
  const normalized = value.trim().replace('#', '');

  if (/^[0-9a-fA-F]{3}$/.test(normalized)) {
    return {
      r: parseInt(normalized[0] + normalized[0], 16),
      g: parseInt(normalized[1] + normalized[1], 16),
      b: parseInt(normalized[2] + normalized[2], 16),
    };
  }

  if (/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16),
    };
  }

  return null;
}

function rgbToCss(color: RGB): string {
  return `rgb(${color.r}, ${color.g}, ${color.b})`;
}

function applyFavicon(faviconUrl: string | undefined) {
  if (!faviconUrl) {
    return;
  }

  document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]').forEach((link) => {
    link.href = faviconUrl;
  });
}

function applyTheme(theme: ThemeConfig) {
  const fallback = { r: 26, g: 59, b: 110 };
  const primary = hexToRgb(theme.primaryColor) ?? fallback;

  document.documentElement.style.setProperty('--primary-color', rgbToCss(primary));
  document.documentElement.style.setProperty('--primary-color-rgb', `${primary.r} ${primary.g} ${primary.b}`);

  // Per-slot color overrides — remove the property when not configured so the
  // CSS :root fallback applies, leaving existing clients visually unchanged.
  const setOrRemove = (varName: string, value: string | undefined) => {
    if (value) {
      document.documentElement.style.setProperty(varName, value);
    } else {
      document.documentElement.style.removeProperty(varName);
    }
  };

  setOrRemove('--portal-name-color', theme.colors?.portalName);
  setOrRemove('--portal-title-color', theme.colors?.title);
  setOrRemove('--portal-subtitle-color', theme.colors?.subtitle);
  setOrRemove('--portal-background', theme.colors?.background);

  // Accent: set both the color and its RGB components for opacity-based usages
  // (badge backgrounds, radio borders). Falls back to primaryColor when unset.
  if (theme.colors?.accent) {
    const accent = hexToRgb(theme.colors.accent);
    if (accent) {
      document.documentElement.style.setProperty('--portal-accent-color', rgbToCss(accent));
      document.documentElement.style.setProperty('--portal-accent-color-rgb', `${accent.r} ${accent.g} ${accent.b}`);
    } else {
      document.documentElement.style.setProperty('--portal-accent-color', theme.colors.accent);
      document.documentElement.style.removeProperty('--portal-accent-color-rgb');
    }
  } else {
    document.documentElement.style.removeProperty('--portal-accent-color');
    document.documentElement.style.removeProperty('--portal-accent-color-rgb');
  }

  // Stepper: independent color for the progress tracker; falls back to --portal-accent-color.
  setOrRemove('--portal-stepper-color', theme.colors?.stepper);
}

export function useProgramConfig(enabled = true) {
  // Keyed by the portal in the address bar, the same path fetchConfig uses, so every route of a page
  // load shares one cache entry and navigating never refetches.
  const portalScope = getPortalPathInfo().basename;

  const query = useQuery({
    queryKey: ['config', portalScope],
    queryFn: fetchConfig,
    enabled,
    // A missing portal will not appear on retry; any other error is retried once.
    retry: (failureCount, error) => !isPortalNotFound(error) && failureCount < 1,
  });

  useEffect(() => {
    if (query.data?.theme) {
      applyTheme(query.data.theme);
      applyFavicon(query.data.theme.faviconUrl);
      document.title = query.data.theme.displayName;
    }
  }, [query.data]);

  return query;
}